import React, { useState, useEffect } from 'react';
import { X, Download, Trash2, Copy, CheckCircle, AlertCircle, AlertTriangle, Info, Wifi, WifiOff, Server, Smartphone } from 'lucide-react';
import { storage } from '../adapters/storage';
import { API_URL } from '../config/constants';

interface DebugEntry {
  id: string;
  timestamp: string;
  level: 'info' | 'success' | 'warning' | 'error';
  category: 'network' | 'auth' | 'sync' | 'storage' | 'system' | 'ui';
  message: string;
  data?: any;
  duration?: number;
}

interface ConnectionStatus {
  apiBaseUrl: string;
  networkStatus: 'online' | 'offline' | 'poor';
  zerotierIP: string;
  backendReachable: boolean;
  lastCheck: string;
  responseTime?: number;
}

interface DebugPanelProps {
  visible: boolean;
  onClose: () => void;
}

// Global debug log storage
let globalDebugLogs: DebugEntry[] = [];
let debugLogCallback: ((entry: DebugEntry) => void) | null = null;

export const addDebugLog = (entry: Omit<DebugEntry, 'id' | 'timestamp'>) => {
  const logEntry: DebugEntry = {
    ...entry,
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    timestamp: new Date().toISOString()
  };

  globalDebugLogs = [...globalDebugLogs.slice(-199), logEntry]; // Keep last 200 logs

  // Store in device storage
  try {
    storage.setItem('debug_logs', JSON.stringify(globalDebugLogs));
  } catch (error) {
    console.error('Failed to store debug logs:', error);
  }

  // Notify callback if set
  if (debugLogCallback) {
    debugLogCallback(logEntry);
  }

  // Also log to console
  const icon = getLogIcon(entry.level);
  console.log(`[MOBILE DEBUG] ${icon} [${entry.category.toUpperCase()}] ${entry.message}`, entry.data || '');
};

export const initializeDebugLogging = (callback?: (entry: DebugEntry) => void) => {
  debugLogCallback = callback || null;

  // Load existing logs from storage
  storage.getItem('debug_logs').then(storedLogs => {
    if (storedLogs) {
      try {
        globalDebugLogs = JSON.parse(storedLogs);
        console.log('🔍 DEBUG: Loaded', globalDebugLogs.length, 'existing debug logs');
      } catch (error) {
        console.error('Failed to parse stored debug logs:', error);
        globalDebugLogs = [];
      }
    }
  }).catch(error => {
    console.error('Failed to load debug logs from storage:', error);
  });
};

export const clearDebugLogs = () => {
  globalDebugLogs = [];
  storage.removeItem('debug_logs');
  console.log('🔍 DEBUG: Debug logs cleared');
};

export const getDebugLogs = () => [...globalDebugLogs];

const getLogIcon = (level: string) => {
  switch (level) {
    case 'error': return '❌';
    case 'warning': return '⚠️';
    case 'success': return '✅';
    default: return 'ℹ️';
  }
};

const getLogColor = (level: string) => {
  switch (level) {
    case 'error': return '#EF4444';
    case 'warning': return '#F59E0B';
    case 'success': return '#10B981';
    default: return '#3B82F6';
  }
};

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'network': return <Wifi className="w-4 h-4" />;
    case 'auth': return <Server className="w-4 h-4" />;
    case 'sync': return <Download className="w-4 h-4" />;
    case 'storage': return <Smartphone className="w-4 h-4" />;
    case 'ui': return <Info className="w-4 h-4" />;
    default: return <AlertCircle className="w-4 h-4" />;
  }
};

export const DebugPanel: React.FC<DebugPanelProps> = ({ visible, onClose }) => {
  const [logs, setLogs] = useState<DebugEntry[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>({
    apiBaseUrl: '',
    networkStatus: 'unknown',
    zerotierIP: 'unknown',
    backendReachable: false,
    lastCheck: 'never'
  });
  const [filter, setFilter] = useState<'all' | 'error' | 'warning'>('all');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (visible) {
      // Initialize debug logging with callback
      initializeDebugLogging((entry) => {
        setLogs(prev => [...prev.slice(-199), entry]);
      });

      // Load existing logs
      setLogs(getDebugLogs());

      // Start connection monitoring
      checkConnection();
      const interval = setInterval(checkConnection, 30000); // Check every 30s

      return () => {
        clearInterval(interval);
        debugLogCallback = null;
      };
    }
  }, [visible]);

  const checkConnection = async () => {
    try {
      const startTime = Date.now();
      const apiUrl = API_URL;

      addDebugLog({
        level: 'info',
        category: 'network',
        message: 'Checking connection status...',
        data: { apiUrl }
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(`${apiUrl}/health`, {
        method: 'GET',
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const duration = Date.now() - startTime;
      const isReachable = response.ok;

      setConnectionStatus(prev => ({
        ...prev,
        apiBaseUrl: apiUrl,
        backendReachable: isReachable,
        networkStatus: isReachable ? 'online' : 'offline',
        lastCheck: new Date().toLocaleTimeString(),
        responseTime: duration
      }));

      if (isReachable) {
        const health = await response.json();
        addDebugLog({
          level: 'success',
          category: 'network',
          message: 'Backend reachable',
          data: {
            status: health.status,
            responseTime: `${duration}ms`,
            url: apiUrl
          }
        });
      } else {
        throw new Error(`HTTP ${response.status}`);
      }
    } catch (error: any) {
      setConnectionStatus(prev => ({
        ...prev,
        backendReachable: false,
        networkStatus: 'offline',
        lastCheck: new Date().toLocaleTimeString(),
        responseTime: undefined
      }));

      addDebugLog({
        level: 'error',
        category: 'network',
        message: 'Backend unreachable',
        data: {
          error: error.message,
          url: API_URL
        }
      });
    }
  };

  const exportLogs = async () => {
    try {
      const filteredLogs = filter === 'all'
        ? logs
        : logs.filter(log => log.level === filter);

      const logsText = `=== logmed MOBILE DEBUG LOGS ===\n` +
        `Generated: ${new Date().toISOString()}\n` +
        `Total Logs: ${filteredLogs.length}\n` +
        `Filter: ${filter}\n\n` +
        filteredLogs.map(log =>
          `[${log.timestamp}] [${log.level.toUpperCase()}] [${log.category}] ${log.message}\n${log.data ? JSON.stringify(log.data, null, 2) : ''}`
        ).join('\n\n');

      // Create downloadable file
      const blob = new Blob([logsText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logmed-debug-logs-${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addDebugLog({
        level: 'info',
        category: 'system',
        message: 'Debug logs exported',
        data: {
          logCount: filteredLogs.length,
          filter: filter,
          filename: a.download
        }
      });
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  const copyLogsToClipboard = async () => {
    try {
      const filteredLogs = filter === 'all'
        ? logs
        : logs.filter(log => log.level === filter);

      const logsText = filteredLogs.map(log =>
        `[${new Date(log.timestamp).toLocaleTimeString()}] [${log.level.toUpperCase()}] [${log.category}] ${log.message}`
      ).join('\n');

      await navigator.clipboard.writeText(logsText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      addDebugLog({
        level: 'info',
        category: 'system',
        message: 'Logs copied to clipboard',
        data: { logCount: filteredLogs.length }
      });
    } catch (error) {
      console.error('Failed to copy logs:', error);
    }
  };

  const clearLogs = () => {
    clearDebugLogs();
    setLogs([]);
    addDebugLog({
      level: 'info',
      category: 'system',
      message: 'Debug logs cleared by user'
    });
  };

  const filteredLogs = filter === 'all'
    ? logs
    : logs.filter(log => log.level === filter);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-80 z-[9999] flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-4xl h-[80vh] max-h-[800px] flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700 bg-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
              <AlertCircle className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Debug Panel</h2>
              <p className="text-xs text-gray-400">
                {filteredLogs.length} logs ({filter === 'all' ? 'all levels' : `${filter} only`})
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'error' | 'warning')}
              className="bg-gray-700 text-white text-sm px-3 py-1 rounded border border-gray-600"
            >
              <option value="all">All Logs</option>
              <option value="error">Errors Only</option>
              <option value="warning">Warnings Only</option>
            </select>

            <button
              onClick={copyLogsToClipboard}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Copy logs to clipboard"
            >
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </button>

            <button
              onClick={exportLogs}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Export logs"
            >
              <Download className="w-4 h-4" />
            </button>

            <button
              onClick={clearLogs}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
              title="Clear logs"
            >
              <Trash2 className="w-4 h-4" />
            </button>

            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Connection Status */}
        <div className="p-4 bg-gray-800 border-b border-gray-700">
          <h3 className="text-sm font-semibold text-gray-300 mb-3">Connection Status</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="bg-gray-700 rounded p-2">
              <div className="text-gray-400 mb-1">API URL</div>
              <div className="text-white font-mono text-xs break-all">
                {connectionStatus.apiBaseUrl || 'Checking...'}
              </div>
            </div>

            <div className="bg-gray-700 rounded p-2">
              <div className="text-gray-400 mb-1">Backend</div>
              <div className={`flex items-center gap-1 ${
                connectionStatus.backendReachable ? 'text-green-400' : 'text-red-400'
              }`}>
                {connectionStatus.backendReachable ? <CheckCircle className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {connectionStatus.backendReachable ? 'Connected' : 'Disconnected'}
              </div>
            </div>

            <div className="bg-gray-700 rounded p-2">
              <div className="text-gray-400 mb-1">Response Time</div>
              <div className="text-white">
                {connectionStatus.responseTime ? `${connectionStatus.responseTime}ms` : 'N/A'}
              </div>
            </div>

            <div className="bg-gray-700 rounded p-2">
              <div className="text-gray-400 mb-1">Last Check</div>
              <div className="text-white">{connectionStatus.lastCheck}</div>
            </div>
          </div>
        </div>

        {/* Logs */}
        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-y-auto p-4 space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-400">No logs available</p>
                <p className="text-xs text-gray-500 mt-1">
                  {filter === 'all' ? 'Start using the app to see debug information' : `No ${filter} logs found`}
                </p>
              </div>
            ) : (
              filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className="bg-gray-800 rounded-lg p-3 border-l-4"
                  style={{ borderLeftColor: getLogColor(log.level) }}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 ${
                      log.level === 'error' ? 'text-red-400' :
                      log.level === 'warning' ? 'text-yellow-400' :
                      log.level === 'success' ? 'text-green-400' :
                      'text-blue-400'
                    }`}>
                      {getLogIcon(log.level)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-1 rounded-full bg-gray-700 text-gray-300 flex items-center gap-1`}>
                          {getCategoryIcon(log.category)}
                          {log.category.toUpperCase()}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        {log.duration && (
                          <span className="text-xs text-gray-500">
                            ({log.duration}ms)
                          </span>
                        )}
                      </div>

                      <p className="text-white text-sm mb-1">{log.message}</p>

                      {log.data && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-gray-400 hover:text-white">
                            View Data ({Object.keys(log.data).length} fields)
                          </summary>
                          <pre className="mt-2 p-2 bg-gray-900 rounded text-gray-300 overflow-x-auto">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebugPanel;